<script lang="ts">
  // A titled group of related rows or content: settings groups, the client
  // "danger zone", detail panels.
  //   <Section title="Werkweek" description="…">
  //     <ListRow label="Uren per week" for="hours"><Input id="hours" … /></ListRow>
  //     <ListRow label="…">…</ListRow>
  //   </Section>
  //   <Section title="Gevarenzone" tone="danger">…</Section>
  //   <Section title="Notities" padded>free content with card padding</Section>
  // variant "card" (default) draws a card with a tinted header; "plain" only
  // the heading (for sections inside a card or dialog).
  import type { Snippet } from 'svelte';

  interface Props {
    title?: string;
    description?: string;
    tone?: 'default' | 'danger';
    variant?: 'card' | 'plain';
    /** Pad the body (for free content instead of ListRows). */
    padded?: boolean;
    /** Heading level of the title (default h3). */
    level?: 2 | 3 | 4;
    id?: string;
    class?: string;
    actions?: Snippet;
    footer?: Snippet;
    children: Snippet;
  }
  let { title, description, tone = 'default', variant = 'card', padded = false, level = 3, id, class: cls = '', actions, footer, children }: Props = $props();
</script>

<section class="section {variant} {cls}" class:card={variant === 'card'} class:danger={tone === 'danger'} {id} aria-label={title}>
  {#if title || actions}
    <header class="head">
      <div class="titles">
        {#if title}<svelte:element this={`h${level}`} class="title">{title}</svelte:element>{/if}
        {#if description}<p class="desc">{description}</p>{/if}
      </div>
      {#if actions}<div class="acts">{@render actions()}</div>{/if}
    </header>
  {/if}
  <div class="rows" class:padded>{@render children()}</div>
  {#if footer}<footer class="foot">{@render footer()}</footer>{/if}
</section>

<style>
  .section { min-width: 0; }
  .card { overflow: hidden; }
  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-3);
  }
  .card .head {
    padding: var(--space-4) var(--card-pad) var(--space-3);
    border-bottom: 1px solid var(--border);
    background: var(--surface-2);
  }
  .plain .head { padding-bottom: var(--space-2); }
  .titles { min-width: 0; display: flex; flex-direction: column; gap: var(--space-0_5); }
  .title { font-family: var(--font-sans); font-size: var(--text-md); font-weight: var(--weight-semibold); letter-spacing: var(--tracking-snug); }
  .desc { font-size: var(--text-sm); color: var(--text-2); }
  .acts { display: flex; gap: var(--space-1_5); flex-shrink: 0; align-items: center; }
  .rows { min-width: 0; }
  .padded { padding: var(--card-pad); }
  .plain .padded { padding: var(--space-2) 0 0; }
  .foot { display: flex; gap: var(--space-2); justify-content: flex-end; flex-wrap: wrap; padding: var(--space-3) var(--card-pad); border-top: 1px solid var(--border); }

  .danger.card { border-color: color-mix(in srgb, var(--danger) 35%, var(--border)); }
  .danger.card .head { background: color-mix(in srgb, var(--danger) 5%, var(--surface)); }
  .danger .title { color: var(--danger); }
</style>
